import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { UserContext } from "./UserContext";

// Private owner QA simulation; never mutates real billing.
const OWNER_EMAIL = "lucas7602@gmail.com";
const STORAGE_PREFIX = "chesshood:owner-qa:v1:";
const QA_DAY_MS = 10_000;
const QA_FREE_SESSION_MS = 10_000;
const QA_FREE_COOLDOWN_MS = 20_000;
const QA_PAID_SESSION_MS = 30_000;
const PASS_MODES = ["random", "personalised", "hood", "lichess"];

function freshConfig() {
  const now = Date.now();
  return {
    enabled: false,
    rating: 1800,
    accountAgeDays: 30,
    subscriptionMode: "real",
    puzzleTrainingMode: "real", // Independent £0.90 web-puzzle simulation.
    longGameMode: "real",
    longGameTrialDaysUsed: 0,
    trialStartedAt: now,
    freeStartedAt: 0,
    passes: Object.fromEntries(PASS_MODES.map((mode) => [mode, { state: "none", startedAt: 0 }])),
  };
}

function storageKey(uid) {
  return `${STORAGE_PREFIX}${uid || "guest"}`;
}

function sanitizeRating(value) {
  const number = Math.round(Number(value));
  return Number.isFinite(number) ? Math.max(0, Math.min(3000, number)) : 1800;
}

function sanitizeAccountAgeDays(value) {
  const number = Math.round(Number(value));
  return Number.isFinite(number) ? Math.max(0, Math.min(365, number)) : 30;
}

function sanitizeLongGameTrialDaysUsed(value) {
  const number = Number(value);
  return Number.isFinite(number) ? Math.max(0, Math.min(7, number)) : 0;
}

function normalizePasses(value) {
  const source = value && typeof value === "object" ? value : {};
  return Object.fromEntries(PASS_MODES.map((mode) => {
    const item = source[mode] && typeof source[mode] === "object" ? source[mode] : {};
    const state = ["none", "ready", "active", "expired"].includes(item.state) ? item.state : "none";
    return [mode, { state, startedAt: Number(item.startedAt || 0) || 0 }];
  }));
}

function normalizeConfig(value) {
  const base = freshConfig();
  const source = value && typeof value === "object" ? value : {};
  const subscriptionMode = ["real", "free", "trial", "pro"].includes(source.subscriptionMode)
    ? source.subscriptionMode
    : "real";
  const longGameMode = ["real", "trial", "subscribed", "expired"].includes(source.longGameMode)
    ? source.longGameMode
    : "real";
  return {
    ...base,
    ...source,
    enabled: Boolean(source.enabled),
    rating: sanitizeRating(source.rating),
    accountAgeDays: sanitizeAccountAgeDays(source.accountAgeDays),
    subscriptionMode,
    puzzleTrainingMode: ["real", "free", "pro"].includes(source.puzzleTrainingMode)
      ? source.puzzleTrainingMode : "real",
    longGameMode,
    longGameTrialDaysUsed: sanitizeLongGameTrialDaysUsed(source.longGameTrialDaysUsed),
    trialStartedAt: Number(source.trialStartedAt || base.trialStartedAt) || base.trialStartedAt,
    freeStartedAt: Number(source.freeStartedAt || 0) || 0,
    passes: normalizePasses(source.passes),
  };
}

function readConfig(uid) {
  if (typeof window === "undefined" || !uid) return freshConfig();
  try {
    return normalizeConfig(JSON.parse(window.localStorage.getItem(storageKey(uid)) || "null"));
  } catch {
    return freshConfig();
  }
}

function writeConfig(uid, config) {
  if (typeof window === "undefined" || !uid) return;
  try {
    window.localStorage.setItem(storageKey(uid), JSON.stringify(normalizeConfig(config)));
  } catch {}
}

function freeAccess(config, now) {
  const startedAt = Number(config.freeStartedAt || 0);
  if (!startedAt || now - startedAt >= QA_FREE_SESSION_MS + QA_FREE_COOLDOWN_MS) {
    return {
      phase: "ready",
      allowed: true,
      label: "FREE READY",
      detail: "Free Hood AI window is ready. In QA it lasts 10 seconds.",
      secondsRemaining: 0,
      nextAvailableAt: null,
    };
  }
  const age = now - startedAt;
  if (age < QA_FREE_SESSION_MS) {
    return {
      phase: "active",
      allowed: true,
      label: "FREE ACTIVE",
      detail: `Free Hood AI window active · ${Math.max(0, Math.ceil((QA_FREE_SESSION_MS - age) / 1000))}s left`,
      secondsRemaining: Math.max(0, Math.ceil((QA_FREE_SESSION_MS - age) / 1000)),
      nextAvailableAt: null,
    };
  }
  const nextAvailableAt = startedAt + QA_FREE_SESSION_MS + QA_FREE_COOLDOWN_MS;
  return {
    phase: "cooldown",
    allowed: false,
    label: "FREE LOCKED",
    detail: `Free window used · next one in ${Math.max(0, Math.ceil((nextAvailableAt - now) / 1000))}s`,
    secondsRemaining: 0,
    nextAvailableAt,
  };
}

function subscriptionAccess(config, now) {
  if (config.subscriptionMode === "real") {
    return { phase: "real", allowed: true, label: "REAL ACCOUNT", detail: "Using the real account state." };
  }
  if (config.subscriptionMode === "pro") {
    return { phase: "pro", allowed: true, label: "SIMULATED PRO", detail: "QA is simulating a Pro account." };
  }
  if (config.subscriptionMode === "trial") {
    const elapsed = Math.max(0, now - Number(config.trialStartedAt || now));
    const virtualDays = elapsed / QA_DAY_MS;
    if (virtualDays < 7) {
      return {
        phase: "trial",
        allowed: true,
        label: "SIMULATED TRIAL",
        detail: `Trial day ${Math.min(7, virtualDays + 1).toFixed(1)} / 7 · ${Math.max(0, 7 - virtualDays).toFixed(1)} days left`,
        trialDaysElapsed: virtualDays,
      };
    }
    return { ...freeAccess(config, now), detail: `Trial finished · ${freeAccess(config, now).detail}` };
  }
  return freeAccess(config, now);
}

const OwnerQaContext = createContext({
  isOwner: false,
  active: false,
  config: freshConfig(),
  hoodAccess: subscriptionAccess(freshConfig(), Date.now()),
  setEnabled: () => {},
  setRating: () => {},
  setAccountAgeDays: () => {},
  setSubscriptionMode: () => {},
  setPuzzleTrainingMode: () => {},
  setLongGameMode: () => {},
  setLongGameTrialDaysUsed: () => {},
  resetLongGameTrial: () => {},
  resetTrial: () => {},
  advanceDays: () => {},
  claimHoodAccess: () => ({ allowed: true }),
  setPassState: () => {},
  purchasePass: () => {},
  startPass: () => null,
  getPass: () => null,
  clearPasses: () => {},
  resetAll: () => {},
  qaDayMs: QA_DAY_MS,
  qaPaidSessionMs: QA_PAID_SESSION_MS,
});

export function OwnerQaProvider({ children }) {
  const { firebaseUser, user } = useContext(UserContext);
  const email = String(firebaseUser?.email || user?.email || "").trim().toLowerCase();
  const isOwner = email === OWNER_EMAIL;
  const uid = firebaseUser?.uid || "";
  const [config, setConfigState] = useState(() => freshConfig());
  const [clock, setClock] = useState(Date.now());

  useEffect(() => {
    if (!isOwner || !uid) {
      setConfigState(freshConfig());
      return;
    }
    setConfigState(readConfig(uid));
  }, [isOwner, uid]);

  useEffect(() => {
    if (!isOwner || !config.enabled) return undefined;
    const timer = window.setInterval(() => setClock(Date.now()), 500);
    return () => window.clearInterval(timer);
  }, [config.enabled, isOwner]);

  const update = useCallback((updater) => {
    if (!isOwner || !uid) return;
    setConfigState((current) => {
      const next = normalizeConfig(typeof updater === "function" ? updater(current) : updater);
      writeConfig(uid, next);
      setClock(Date.now());
      return next;
    });
  }, [isOwner, uid]);

  const setEnabled = useCallback((enabled) => update((current) => ({ ...current, enabled: Boolean(enabled) })), [update]);
  const setRating = useCallback((rating) => update((current) => ({ ...current, rating: sanitizeRating(rating) })), [update]);
  const setAccountAgeDays = useCallback((accountAgeDays) => update((current) => ({
    ...current,
    accountAgeDays: sanitizeAccountAgeDays(accountAgeDays),
  })), [update]);
  const setSubscriptionMode = useCallback((subscriptionMode) => update((current) => ({
    ...current,
    subscriptionMode,
    trialStartedAt: subscriptionMode === "trial" ? Date.now() : current.trialStartedAt,
    freeStartedAt: subscriptionMode === "free" ? 0 : current.freeStartedAt,
  })), [update]);
  const setPuzzleTrainingMode = useCallback((mode) => update((current) => ({
    ...current,
    puzzleTrainingMode: ["real", "free", "pro"].includes(mode) ? mode : "real",
  })), [update]);

  const setLongGameMode = useCallback((longGameMode) => update((current) => ({
    ...current,
    longGameMode: ["real", "trial", "subscribed", "expired"].includes(longGameMode) ? longGameMode : "real",
    longGameTrialDaysUsed: longGameMode === "trial" ? Math.min(current.longGameTrialDaysUsed || 0, 6.99) : current.longGameTrialDaysUsed,
  })), [update]);
  const setLongGameTrialDaysUsed = useCallback((days) => update((current) => ({
    ...current,
    longGameTrialDaysUsed: sanitizeLongGameTrialDaysUsed(days),
  })), [update]);
  const resetLongGameTrial = useCallback(() => update((current) => ({
    ...current,
    longGameMode: "trial",
    longGameTrialDaysUsed: 0,
  })), [update]);
  const resetTrial = useCallback(() => update((current) => ({ ...current, trialStartedAt: Date.now(), freeStartedAt: 0 })), [update]);
  const advanceDays = useCallback((days = 1) => update((current) => ({
    ...current,
    trialStartedAt: Number(current.trialStartedAt || Date.now()) - Math.max(0, Number(days) || 0) * QA_DAY_MS,
    freeStartedAt: current.freeStartedAt
      ? Number(current.freeStartedAt) - Math.max(0, Number(days) || 0) * QA_DAY_MS
      : 0,
  })), [update]);

  const hoodAccess = useMemo(
    () => subscriptionAccess(config, clock),
    [clock, config]
  );

  const claimHoodAccess = useCallback(() => {
    if (!isOwner || !config.enabled || config.subscriptionMode === "real") return { allowed: true, phase: "real" };
    let gate = subscriptionAccess(config, Date.now());
    if (["ready"].includes(gate.phase)) {
      const next = { ...config, freeStartedAt: Date.now() };
      writeConfig(uid, next);
      setConfigState(next);
      setClock(Date.now());
      gate = subscriptionAccess(next, Date.now());
    }
    return gate;
  }, [config, isOwner, uid]);

  const setPassState = useCallback((mode, state) => {
    if (!PASS_MODES.includes(mode) || !["none", "ready", "active", "expired"].includes(state)) return;
    update((current) => ({
      ...current,
      passes: {
        ...current.passes,
        [mode]: { state, startedAt: state === "active" ? Date.now() : 0 },
      },
    }));
  }, [update]);

  const purchasePass = useCallback((mode) => setPassState(mode, "ready"), [setPassState]);

  const startPass = useCallback((mode) => {
    if (!PASS_MODES.includes(mode)) return null;
    update((current) => ({
      ...current,
      passes: {
        ...current.passes,
        [mode]: { state: "active", startedAt: Date.now() },
      },
    }));
    return { id: `qa-${mode}`, mode, state: "active", qa: true, secondsRemaining: QA_PAID_SESSION_MS / 1000 };
  }, [update]);

  const getPass = useCallback((mode) => {
    if (!PASS_MODES.includes(mode)) return null;
    const pass = config.passes?.[mode] || { state: "none", startedAt: 0 };
    if (pass.state !== "active") return { id: `qa-${mode}`, mode, qa: true, ...pass };
    const secondsRemaining = Math.max(0, Math.ceil((QA_PAID_SESSION_MS - (clock - Number(pass.startedAt || clock))) / 1000));
    return {
      id: `qa-${mode}`,
      mode,
      qa: true,
      ...pass,
      state: secondsRemaining > 0 ? "active" : "expired",
      secondsRemaining,
    };
  }, [clock, config.passes]);

  useEffect(() => {
    if (!isOwner || !config.enabled) return;
    let changed = false;
    const passes = { ...config.passes };
    for (const mode of PASS_MODES) {
      const pass = passes[mode];
      if (pass?.state === "active" && Number(pass.startedAt) && clock - Number(pass.startedAt) >= QA_PAID_SESSION_MS) {
        passes[mode] = { ...pass, state: "expired" };
        changed = true;
      }
    }
    if (changed) update((current) => ({ ...current, passes }));
  }, [clock, config.enabled, config.passes, isOwner, update]);

  const clearPasses = useCallback(() => update((current) => ({
    ...current,
    passes: normalizePasses({}),
  })), [update]);

  const resetAll = useCallback(() => {
    if (!isOwner || !uid) return;
    const next = freshConfig();
    writeConfig(uid, next);
    setConfigState(next);
    setClock(Date.now());
  }, [isOwner, uid]);

  const value = useMemo(() => ({
    isOwner,
    active: isOwner && config.enabled,
    config,
    hoodAccess,
    setEnabled,
    setRating,
    setAccountAgeDays,
    setSubscriptionMode,
    setPuzzleTrainingMode,
    setLongGameMode,
    setLongGameTrialDaysUsed,
    resetLongGameTrial,
    resetTrial,
    advanceDays,
    claimHoodAccess,
    setPassState,
    purchasePass,
    startPass,
    getPass,
    clearPasses,
    resetAll,
    qaDayMs: QA_DAY_MS,
    qaPaidSessionMs: QA_PAID_SESSION_MS,
  }), [
    advanceDays,
    claimHoodAccess,
    clearPasses,
    config,
    getPass,
    hoodAccess,
    isOwner,
    purchasePass,
    resetAll,
    resetTrial,
    setEnabled,
    setPassState,
    setRating,
    setAccountAgeDays,
    setSubscriptionMode,
    setPuzzleTrainingMode,
    setLongGameMode,
    setLongGameTrialDaysUsed,
    resetLongGameTrial,
    startPass,
  ]);

  return <OwnerQaContext.Provider value={value}>{children}</OwnerQaContext.Provider>;
}

export function useOwnerQa() {
  return useContext(OwnerQaContext);
}
